import { useRef, useState } from "react";
import { useParams, useLocation } from "wouter";
import JSZip from "jszip";
import { toast } from "sonner";
import { Download, Loader2, Check, X, Square, Trash2 } from "lucide-react";
import { FileDropZone, downloadBlob } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { useFreemium } from "@/hooks/useFreemium";
import { formatFileSize } from "@/lib/formatFileSize";
import { multiTargets, setHandoff } from "@/lib/handoff";
import { addToBatch, batchLimit, batchTotals, runBatch, uniqueZipNames, TRIAL_BATCH_LIMIT, type BatchItem } from "@/lib/batch";

interface Props {
  accept: string;
  /** What the files are, for messages: "PDF", "photo". */
  noun: string;
  /** Applies the tool's current settings to one file. Throw to fail just that file. */
  process: (file: File, signal?: AbortSignal) => Promise<{ blob: Blob; fileName: string }>;
  /** Return a message to stop the run before it starts (for example a missing watermark text). */
  check?: () => string | null;
  zipName: string;
  runLabel?: string;
  /** Settings shown above the list. */
  children?: React.ReactNode;
  maxSizeMb?: number;
  /** Put the original of every file that failed or was skipped into the ZIP, so nothing goes missing from it. */
  includeOriginals?: boolean;
  /** Disable the run button while `check` returns a message. Only for tools that already show that message next to the setting; otherwise the button would be dead with no explanation. */
  disableOnCheck?: boolean;
}

let nextId = 1;

/**
 * Several files, one setting. Up to 5 files on the trial, more on a paid plan, and the whole batch counts as
 * one daily use. Files run one after another; one that fails does not stop the others; results can be saved one by
 * one or as a single ZIP.
 *
 * The use is charged on delivery, like every other tool (B-0051, B-0055): running the batch is free, a run that
 * fails or produces nothing costs nothing, and the visit's one use is taken when a result is saved, whether that is
 * one file or the ZIP (downloadBlob asks the page's gate, lib/downloadGate.ts; a second save in the same visit is
 * free). At 0 remaining the run still works and the pass dialog appears at the save. Do not take a use in run().
 */
export function BatchRunner({ accept, noun, process, check, zipName, runLabel = "Process all", children, maxSizeMb = 50, includeOriginals = false, disableOnCheck = false }: Props) {
  const { isPro, isAdmin, isLoading } = useFreemium();
  const limit = batchLimit({ isPro, isAdmin, isLoading });
  const [items, setItems] = useState<BatchItem[]>([]);
  const [running, setRunning] = useState(false);
  const stopRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const params = useParams<{ id?: string }>();
  const [, navigate] = useLocation();

  const onFiles = (files: File[]) => {
    const { files: next, dropped } = addToBatch(items.map((i) => i.file), files, limit);
    setItems(next.map((f) => items.find((i) => i.file === f) ?? { id: nextId++, file: f, status: "queued" as const }));
    if (dropped > 0) {
      toast.info(`${dropped} file${dropped === 1 ? "" : "s"} left out`, {
        description: limit <= TRIAL_BATCH_LIMIT ? `A batch takes up to ${limit} files on the trial. A paid plan removes that limit.` : `A batch takes up to ${limit} files.`,
      });
    }
  };

  const run = async () => {
    const problem = check?.();
    if (problem) { toast.error(problem); return; }
    if (items.length === 0) return;
    // No use is taken here: the batch is charged when a result is downloaded (see the note above the component).
    stopRef.current = false;
    const ac = new AbortController();
    abortRef.current = ac;
    setRunning(true);
    try {
      await runBatch(items, process, setItems, () => stopRef.current, ac.signal);
    } finally {
      abortRef.current = null;
      setRunning(false);
    }
  };

  const totals = batchTotals(items);
  const finished = items.length > 0 && !running && items.every((i) => i.status !== "queued" && i.status !== "working");
  const results = items.filter((i) => i.status === "done" && i.result);

  const downloadZip = async () => {
    const zip = new JSZip();
    // Finished files first. With includeOriginals, files that failed or were skipped go in as they were.
    const rest = includeOriginals ? items.filter((i) => i.status !== "done" || !i.result) : [];
    const entries: { name: string; blob: Blob }[] = [
      ...results.map((r) => ({ name: r.result!.fileName, blob: r.result!.blob })),
      ...rest.map((r) => ({ name: r.file.name, blob: r.file as Blob })),
    ];
    const names = uniqueZipNames(entries.map((e) => e.name));
    entries.forEach((e, i) => zip.file(names[i], e.blob));
    downloadBlob(await zip.generateAsync({ type: "blob" }), zipName);
  };

  const next = results.length > 1 && params.id ? multiTargets(results.map((r) => r.result!.fileName)) : [];
  const carryOn = (toolId: string) => {
    const [first, ...rest] = results.map((r) => r.result!);
    setHandoff({ blob: first.blob, fileName: first.fileName, more: rest, fromToolId: params.id!, toToolId: toolId });
    navigate(`/tool/${toolId}`);
  };

  return (
    <div className="space-y-4" data-testid="batch-runner">
      {children}
      {!running && items.length < limit && (
        <FileDropZone accept={accept} multiple deferUsageLog maxSize={maxSizeMb} onFiles={onFiles}
          label={`Drop several ${noun} files`} description={`Up to ${limit} files in one batch${limit <= TRIAL_BATCH_LIMIT ? " on the trial" : ""}. The whole batch counts as one use, taken when you download.`} />
      )}
      {items.length > 0 && (
        <ul className="space-y-2" data-testid="batch-list">
          {items.map((it) => (
            <li key={it.id} className="flex items-center gap-3 p-3 rounded-lg border bg-card text-sm">
              <span className="w-5 shrink-0" aria-hidden>
                {it.status === "working" ? <Loader2 className="h-4 w-4 animate-spin" /> : it.status === "done" ? <Check className="h-4 w-4 text-green-600" /> : it.status === "failed" ? <X className="h-4 w-4 text-destructive" /> : null}
              </span>
              <span className="flex-1 min-w-0 truncate">{it.file.name} <span className="text-muted-foreground">({formatFileSize(it.file.size)})</span></span>
              <span className="text-xs text-muted-foreground shrink-0" data-testid="batch-status">
                {it.status === "done" && it.result ? `${formatFileSize(it.result.blob.size)}` : it.status === "failed" ? it.error : it.status === "skipped" ? "Stopped, not processed" : it.status === "working" ? "Working..." : "Waiting"}
              </span>
              {it.status === "done" && it.result && (
                <Button size="sm" variant="ghost" onClick={() => downloadBlob(it.result!.blob, it.result!.fileName)} aria-label={`Download ${it.result.fileName}`}><Download className="h-4 w-4" /></Button>
              )}
              {!running && it.status !== "done" && (
                <Button size="sm" variant="ghost" aria-label={`Remove ${it.file.name}`} onClick={() => setItems(items.filter((x) => x.id !== it.id))}><Trash2 className="h-4 w-4" /></Button>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap gap-2">
        {running ? (
          <Button variant="outline" onClick={() => { stopRef.current = true; abortRef.current?.abort(); }} className="gap-2" data-testid="batch-stop"><Square className="h-3.5 w-3.5" /> Stop now</Button>
        ) : (
          <Button onClick={run} disabled={items.length === 0 || (disableOnCheck && !!check?.())} data-testid="batch-run">{finished ? "Run again" : `${runLabel} (${items.length})`}</Button>
        )}
        {(results.length > 1 || (includeOriginals && results.length > 0 && items.length > 1)) && (
          <Button variant="secondary" onClick={downloadZip} className="gap-2" data-testid="batch-zip">
            <Download className="h-4 w-4" /> {running ? "Download finished so far (ZIP)" : "Download all (ZIP)"}
          </Button>
        )}
        {!running && next.map((n) => (
          <Button key={n.toolId} variant="outline" onClick={() => carryOn(n.toolId)} data-testid={`batch-continue-${n.toolId}`}>Continue with {n.name}</Button>
        ))}
        {items.length > 0 && !running && <Button variant="ghost" onClick={() => setItems([])}>Clear</Button>}
      </div>
      {finished && (
        <p className="text-sm text-muted-foreground" aria-live="polite" data-testid="batch-summary">
          {totals.done} of {items.length} done{totals.failed > 0 ? `, ${totals.failed} failed` : ""}.
          {totals.done > 0 && totals.before !== totals.after ? ` ${formatFileSize(totals.before)} to ${formatFileSize(totals.after)} in total.` : ""}
        </p>
      )}
    </div>
  );
}

/** "One file / Several files" switch shown at the top of a tool that offers batch mode. */
export function BatchModeToggle({ batch, onChange, limitText }: { batch: boolean; onChange: (batch: boolean) => void; limitText?: string }) {
  const { isPro, isAdmin, isLoading } = useFreemium();
  const limit = batchLimit({ isPro, isAdmin, isLoading });
  return (
    <div className="flex flex-wrap items-center gap-2 mb-4" role="group" aria-label="How many files">
      <Button size="sm" variant={batch ? "outline" : "default"} onClick={() => onChange(false)} aria-pressed={!batch}>One file</Button>
      <Button size="sm" variant={batch ? "default" : "outline"} onClick={() => onChange(true)} aria-pressed={batch} data-testid="batch-toggle">Several files</Button>
      {batch && <span className="text-xs text-muted-foreground">{limitText ?? `Up to ${limit} files, same settings for each, counts as one use when you download.`}</span>}
    </div>
  );
}
